// Keyboard shortcut parsing and matching. Pure functions; the registry that
// uses them lives in ShortcutContext.js.
//
// A shortcut is written the way people read it:
//   'mod+k'   ⌘K on a Mac, Ctrl+K elsewhere
//   '?'       a single printable key
//   'g a'     a chord: press g, then a, within a second
//   'shift+/' modifiers joined with +, chord steps separated by a space

export const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || '');

function parseCombo(combo) {
  const tokens = combo.split('+');
  const rawKey = tokens.pop();
  const key = rawKey.length === 1 ? rawKey : rawKey.toLowerCase();
  const mods = { meta: false, ctrl: false, alt: false, shift: false };
  for (const token of tokens) {
    const t = token.toLowerCase();
    if (t === 'mod') { if (isMac) mods.meta = true; else mods.ctrl = true; }
    else if (t === 'cmd' || t === 'meta' || t === 'command') mods.meta = true;
    else if (t === 'ctrl' || t === 'control') mods.ctrl = true;
    else if (t === 'alt' || t === 'option') mods.alt = true;
    else if (t === 'shift') mods.shift = true;
  }
  return { key, ...mods };
}

/** 'g a' → [{key:'g'…}, {key:'a'…}]; 'mod+k' → [{key:'k', meta:true…}] */
export function parseShortcut(keys) {
  return String(keys).trim().split(/\s+/).map(parseCombo);
}

/** Does this keydown event match one step of a shortcut? */
export function eventMatches(event, combo) {
  const eventKey = event.key || '';
  const key = eventKey.length === 1 ? eventKey : eventKey.toLowerCase();
  if (combo.meta !== !!event.metaKey || combo.ctrl !== !!event.ctrlKey || combo.alt !== !!event.altKey) return false;
  if (combo.key.length === 1) {
    // Shift is baked into the character for printable keys ('?' is shift+/),
    // so compare the character; with an explicit shift modifier compare
    // case-insensitively, since shift+k arrives as 'K'.
    if (combo.shift) return !!event.shiftKey && key.toLowerCase() === combo.key.toLowerCase();
    return key === combo.key;
  }
  if (combo.shift !== !!event.shiftKey) return false;
  return key === combo.key;
}

/** Is the event target somewhere typing belongs? */
export function isEditableTarget(target) {
  if (!target || !target.tagName) return false;
  const tag = target.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  return !!target.isContentEditable;
}

const KEY_LABELS = {
  arrowup: '↑', arrowdown: '↓', arrowleft: '←', arrowright: '→',
  enter: '↵', escape: 'Esc', backspace: '⌫', ' ': 'Space',
};

function comboLabel(combo) {
  const parts = [];
  if (combo.ctrl) parts.push(isMac ? '⌃' : 'Ctrl');
  if (combo.alt) parts.push(isMac ? '⌥' : 'Alt');
  if (combo.shift) parts.push(isMac ? '⇧' : 'Shift');
  if (combo.meta) parts.push(isMac ? '⌘' : 'Win');
  const key = KEY_LABELS[combo.key] || (combo.key.length === 1 ? combo.key.toUpperCase() : combo.key);
  parts.push(key);
  return isMac ? parts.join('') : parts.join('+');
}

/** For display: 'mod+k' → '⌘K' / 'Ctrl+K'; 'g a' → ['G', 'A'] (then). */
export function formatShortcut(keys) {
  return parseShortcut(keys).map(comboLabel);
}
